import { defineStore } from 'pinia'
import { apiWithoutBaseURL } from '@/services/api'
import graphService from '@/services/graphService'

export const useKnowledgeGraphStore = defineStore('knowledgeGraph', {
    state: () => ({
        // 节点数据
        nodes: [],
        // 边数据
        edges: [],
        // 分组数据
        groups: [
            { id: 'default', name: '默认分组', color: '#5B8FF9' },
            { id: 'important', name: '重要节点', color: '#FF6B3B' },
            { id: 'secondary', name: '次要节点', color: '#61DDAA' }
        ],
        // 当前选中的节点
        selectedNode: null,
        // 当前选中的边
        selectedEdge: null,
        // 筛选条件
        filters: {
            groups: ["default"], // 选中的分组
            searchText: ''
        },
        // 图布局配置
        layoutConfig: {
            type: 'force',
            treeDirection: 'left-to-right', // 树形布局方向
            preventOverlap: true,
            nodeSize: 30
        },
        // 缓存图数据版本
        dataVersion: 0,
        // 标签数据
        tags: [],
        // 关系类型数据
        graphTypes: [],
        // 当前选中的标签
        selectedTags: []
    }),

    getters: {
        // 获取过滤后的节点
        filteredNodes: (state) => {
            let nodes = state.nodes

            // 按分组筛选 (groups) 或 标签筛选 (tags)
            // 只有当tags或groups被选择时才显示数据，如果都不符合应该不显示任何数据
            const hasGroupFilter = state.filters.groups.length > 0;
            const hasTagFilter = state.selectedTags.length > 0;

            // 如果有筛选条件（标签或分组被选中），则进行筛选
            // 否则不显示任何节点
            if (hasGroupFilter || hasTagFilter) {
                nodes = nodes.filter(node => {
                    // 检查是否符合分组条件
                    const matchesGroup = hasGroupFilter &&
                        node.groups &&
                        node.groups.some(group => state.filters.groups.includes(group));

                    // 检查是否符合标签条件 (node.category 对应标签)
                    let matchesTag = false;
                    if (hasTagFilter && node.category !== undefined) {
                        // 处理category为数组的情况（示例数据）
                        if (Array.isArray(node.category)) {
                            matchesTag = node.category.some(cat =>
                                state.tags[cat] && state.selectedTags.includes(state.tags[cat].name)
                            );
                        }
                        // 处理category为数字索引的情况（API数据）
                        else if (typeof node.category === 'number') {
                            matchesTag = state.tags[node.category] &&
                                state.selectedTags.includes(state.tags[node.category].name);
                        }
                    }

                    // 或关系：满足分组条件或标签条件之一即可
                    return matchesGroup || matchesTag;
                });
            } else {
                // 如果没有任何筛选条件被选中，则不显示任何节点
                nodes = [];
            }

            // 注意：我们不再在这里根据搜索文本筛选节点
            // 搜索文本仅用于高亮显示，不影响节点的显示/隐藏
            // 搜索功能由图形组件通过调整透明度来实现

            return nodes
        },

        // 获取过滤后的边
        filteredEdges: (state) => {
            const nodeIds = state.filteredNodes.map(node => node.id)
            return state.edges.filter(edge =>
                nodeIds.includes(edge.source) && nodeIds.includes(edge.target)
            )
        },

        // 获取图数据
        graphData: (state) => {
            return {
                nodes: state.filteredNodes,
                edges: state.filteredEdges
            }
        },

        // 获取选中节点的相关数据
        selectedNodeRelatedData: (state) => {
            if (!state.selectedNode) return { nodes: [], edges: [] }

            const relatedEdges = state.edges.filter(edge =>
                edge.source === state.selectedNode.id || edge.target === state.selectedNode.id
            )

            const relatedNodeIds = new Set([state.selectedNode.id])
            relatedEdges.forEach(edge => {
                relatedNodeIds.add(edge.source)
                relatedNodeIds.add(edge.target)
            })

            const relatedNodes = state.nodes.filter(node =>
                relatedNodeIds.has(node.id)
            )

            return {
                nodes: relatedNodes,
                edges: relatedEdges
            }
        }
    },

    actions: {
        // 添加节点
        addNode(node) {
            const existingNodeIds = new Set(this.nodes.map(node => node.id))
            if (!existingNodeIds.has(node.id)) {
                const newNode = {
                    id: String(node.id) || `node_${Date.now()}`,
                    name: node.name || node.label, // 改为 name
                    desc: node.desc || node.description || '', // 改为 desc
                    category: node.category || [], // 改为 category
                    groups: node.groups || ['default'],
                    imageUrl: node.imageUrl || '',
                    locked: node.locked || false,
                    style: {
                        fill: this.getGroupColor(node.groups?.[0] || 'default'),
                        stroke: '#fff',
                        lineWidth: 2,
                        ...node.style
                    },
                    x: node.x || Math.random() * 800,
                    y: node.y || Math.random() * 600,
                    symbolSize: node.symbolSize || node.size || 30 // 改为 symbolSize
                }

                this.nodes.push(newNode)
                this.saveData()
            }
            //
        },

        // 更新节点
        updateNode(nodeId, updates) {
            const nodeIndex = this.nodes.findIndex(node => node.id === nodeId)
            console.log("updateNode:", nodeId, updates)
            if (nodeIndex !== -1) {


                // 保存原始节点数据用于比较
                const originalNode = { ...this.nodes[nodeIndex] }

                // 处理属性名称映射
                const mappedUpdates = {
                    ...updates
                }

                // 如果有 groups 属性，同时保留groups和映射为category（用于兼容性）
                // if (updates.groups !== undefined) {
                //     mappedUpdates.category = updates.groups
                //     // 不删除groups属性，直接保留
                // }

                this.nodes[nodeIndex] = { ...this.nodes[nodeIndex], ...mappedUpdates }

                // 确保响应式更新 - 创建新的节点对象引用
                this.nodes.splice(nodeIndex, 1, { ...this.nodes[nodeIndex] })


                // 检查name和desc是否发生变化，如果变化则调用modifyNode
                if ((updates.name !== undefined && originalNode.name !== updates.name) ||
                    (updates.desc !== undefined && originalNode.desc !== updates.desc)) {
                    // 调用graphService的modifyNode方法更新数据库
                    graphService.modifyNode(nodeId, updates.name || originalNode.name, updates.desc || originalNode.desc)
                        .catch(error => {
                            console.error('更新节点到数据库失败:', error)
                        })
                }
                console.log("after updateNode:", this.nodes[nodeIndex])
                this.saveData()
            }
        },

        // 删除节点
        deleteNode(nodeId) {
            this.nodes = this.nodes.filter(node => node.id !== nodeId)
            // 同时删除相关的边
            this.edges = this.edges.filter(edge =>
                edge.source !== nodeId && edge.target !== nodeId
            )
            if (this.selectedNode?.id === nodeId) {
                this.selectedNode = null
            }
            this.saveData()
        },

        // 添加边
        addEdge(edge) {
            const newEdge = {
                id: edge.id || `edge_${Date.now()}`,
                source: edge.source,
                target: edge.target,
                label: edge.label || '',
                value: edge.value || 1,
                graphId: edge.graphId,
                style: {
                    stroke: '#A3B1BF',
                    lineWidth: edge.value || 2,
                    ...edge.style
                }
            }
            this.edges.push(newEdge)
            this.saveData()
        },

        // 更新边
        updateEdge(edgeId, updates) {
            const edgeIndex = this.edges.findIndex(edge => edge.id === edgeId)
            if (edgeIndex !== -1) {
                this.edges[edgeIndex] = { ...this.edges[edgeIndex], ...updates }
                this.saveData()
            }
        },

        // 删除边
        deleteEdge(edgeId) {
            this.edges = this.edges.filter(edge => edge.id !== edgeId)
            if (this.selectedEdge?.id === edgeId) {
                this.selectedEdge = null
            }
            this.saveData()
        },

        // 设置选中节点
        setSelectedNode(node) {
            this.selectedNode = node
        },

        // 设置选中边
        setSelectedEdge(edge) {
            this.selectedEdge = edge
        },

        // 清除选中
        clearSelection() {
            this.selectedNode = null
            this.selectedEdge = null
        },

        // 设置筛选条件
        setFilters(filters) {
            this.filters = { ...this.filters, ...filters }
        },

        // 更新分组
        updateGroup(groupId, updates) {
            const groupIndex = this.groups.findIndex(group => group.id === groupId)
            if (groupIndex !== -1) {
                this.groups[groupIndex] = { ...this.groups[groupIndex], ...updates }
                // 更新使用该分组的节点颜色
                this.nodes.forEach(node => {
                    // 检查节点是否属于该分组（同时检查category和groups）
                    if (node.groups && node.groups.includes(groupId)) {
                        node.style.fill = updates.color || this.groups[groupIndex].color
                    }
                })
                this.saveData()
            }
        },

        // 添加分组
        addGroup(group) {
            const newGroup = {
                id: group.id || `group_${Date.now()}`,
                name: group.name,
                color: group.color || '#5B8FF9'
            }
            this.groups.push(newGroup)
            this.saveData()
        },

        // 删除分组(暂未实现)
        deleteGroup(groupId) {
            this.groups = this.groups.filter(group => group.id !== groupId)
            // 从节点中移除该分组
            this.nodes.forEach(node => {
                node.groups = node.groups.filter(group => group !== groupId) // 改为 category
                if (node.groups.length === 0) { // 改为 category
                    node.groups.push('default') // 改为 category
                }
            })
            this.saveData()
        },

        // 获取分组颜色
        getGroupColor(groupId) {
            const group = this.groups.find(group => group.id === groupId)
            return group?.color || '#5B8FF9'
        },

        // 初始化示例数据
        initSampleData() {
            const sampleNodes = [
                {
                    id: 'node1',
                    name: '主节点', // 改为 name
                    desc: '这是一个主要节点', // 改为 desc
                    category: ['important'], // 改为 category
                    x: 400,
                    y: 300,
                    symbolSize: 30, // 改为 symbolSize
                    locked: false,
                    style: {
                        fill: this.getGroupColor('important'),
                        stroke: '#fff',
                        lineWidth: 2
                    }
                },
                {
                    id: 'node2',
                    name: '子节点A', // 改为 name
                    desc: '连接到主节点的子节点', // 改为 desc
                    category: ['secondary'], // 改为 category
                    x: 300,
                    y: 200,
                    symbolSize: 30, // 改为 symbolSize
                    locked: false,
                    style: {
                        fill: this.getGroupColor('secondary'),
                        stroke: '#fff',
                        lineWidth: 2
                    }
                },
                {
                    id: 'node3',
                    name: '子节点B', // 改为 name
                    desc: '另一个子节点', // 改为 desc
                    category: ['secondary'], // 改为 category
                    x: 500,
                    y: 200,
                    symbolSize: 30, // 改为 symbolSize
                    locked: false,
                    style: {
                        fill: this.getGroupColor('secondary'),
                        stroke: '#fff',
                        lineWidth: 2
                    }
                },
                {
                    id: 'node4',
                    name: '信息节点', // 改为 name
                    desc: '提供额外信息的节点', // 改为 desc
                    category: ['info'], // 改为 category
                    x: 400,
                    y: 400,
                    symbolSize: 30, // 改为 symbolSize
                    locked: false,
                    style: {
                        fill: this.getGroupColor('info'),
                        stroke: '#fff',
                        lineWidth: 2
                    }
                }
            ]

            const sampleEdges = [
                {
                    id: 'edge1',
                    source: 'node1',
                    target: 'node2',
                    label: '主要连接',
                    description: '',
                    style: {
                        stroke: '#A3B1BF',
                        lineWidth: 2
                    }
                },
                {
                    id: 'edge2',
                    source: 'node1',
                    target: 'node3',
                    label: '次要连接',
                    description: '',
                    style: {
                        stroke: '#A3B1BF',
                        lineWidth: 2
                    }
                },
                {
                    id: 'edge3',
                    source: 'node1',
                    target: 'node4',
                    label: '信息连接',
                    description: '',
                    style: {
                        stroke: '#A3B1BF',
                        lineWidth: 2
                    }
                }
            ]

            // 直接赋值而不调用add方法，避免触发saveData
            this.nodes = sampleNodes
            this.edges = sampleEdges
            // 初始化后保存一次
            this.saveData()
        },

        // 保存数据到本地存储
        saveData() {
            const data = {
                nodes: this.nodes,
                edges: this.edges,
                groups: this.groups
            }
            localStorage.setItem('knowledge-graph-data', JSON.stringify(data))
            // 增加数据版本号
            this.dataVersion++
        },

        // 从本地存储加载数据
        loadData() {
            try {
                const data = localStorage.getItem('knowledge-graph-data')
                if (data) {
                    const parsed = JSON.parse(data)
                    this.nodes = parsed.nodes || []
                    this.edges = parsed.edges || []
                    this.groups = parsed.groups || this.groups
                } else {
                    // 如果没有保存的数据，初始化示例数据
                    this.initSampleData()
                }
                // 初始化时不增加版本号
            } catch (error) {
                console.error('加载图数据失败:', error)
                this.initSampleData()
            }
        },

        // 清空所有数据
        clearData() {
            this.nodes = []
            this.edges = []
            this.selectedNode = null
            this.selectedEdge = null
            this.saveData()
        },

        // 导出数据
        exportData() {
            const data = {
                nodes: this.nodes,
                edges: this.edges,
                groups: this.groups
            }
            return JSON.stringify(data, null, 2)
        },

        // 导入数据
        importData(jsonData) {
            try {
                const data = JSON.parse(jsonData)
                this.nodes = data.nodes || []
                this.edges = data.edges || []
                this.groups = data.groups || this.groups
                this.saveData()
                return true
            } catch (error) {
                console.error('导入数据失败:', error)
                return false
            }
        },

        // ==================== 从 GraphService 整合的方法 ====================

        // 批量添加节点和关系
        async addByBatch(data) {
            try {
                const response = await apiWithoutBaseURL.post('/Graph/api/addByBatch/', data)
                return response.data
            } catch (error) {
                console.error('批量添加失败:', error)
                throw error
            }
        },

        // 修改图谱关系
        async changeGraph(data) {
            try {
                const response = await apiWithoutBaseURL.post('/Graph/api/changeGraph/', data)
                return response.data
            } catch (error) {
                console.error('修改图谱关系失败:', error)
                throw error
            }
        },

        // 翻译单词
        async translateWord(word) {
            try {
                const response = await apiWithoutBaseURL.post('/Graph/api/translateWord/', { word })
                return response.data
            } catch (error) {
                console.error('翻译单词失败:', error)
                throw error
            }
        },

        // 为节点添加标签
        async addTagToNode(nodeId, tagName) {
            try {
                const response = await apiWithoutBaseURL.post('/Graph/api/addTagToNode/', {
                    node_id: nodeId,
                    tag_name: tagName
                })
                return response.data
            } catch (error) {
                console.error('为节点添加标签失败:', error)
                throw error
            }
        },

        // 检查节点是否存在
        async checkNode(nodeName) {
            try {
                const response = await apiWithoutBaseURL.get('/Graph/api/checkNode/', {
                    params: { node_name: nodeName }
                })
                return response.data
            } catch (error) {
                console.error('检查节点失败:', error)
                throw error
            }
        },

        // 查找所有标签【已使用】
        async findTags() {
            try {
                const response = await apiWithoutBaseURL.get('/Graph/api/findTags/')
                console.log('findTags API响应:', response)
                // 根据返回的数据结构，第一个元素是标签列表
                if (response.data && response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
                    this.tags = response.data.data[0] || []
                    console.log('标签数据已更新:', this.tags)
                    // 检查标签数据结构
                    if (this.tags.length > 0) {
                        console.log('第一个标签对象:', this.tags[0])
                        console.log('标签对象类型:', typeof this.tags[0])
                        console.log('标签对象属性:', Object.keys(this.tags[0]))
                    }
                    //节点数据
                    this.nodes = response.data.data[1] || []
                    //使nodes的id都变成字符串类型
                    this.nodes.forEach(node => {
                        node.id = String(node.id)
                    })
                    this.edges = response.data.data[2] || []
                    //关系类型数据
                    this.graphTypes = response.data.data[3] || []
                    console.log('关系类型数据:', this.graphTypes)
                    this.saveData()
                } else {
                    console.warn('findTags返回的数据格式不正确:', response.data)
                }
                return response.data
            } catch (error) {
                console.error('查找标签失败:', error)
                throw error
            }
        },

        // 查找所有标签
        async findAllTags() {
            try {
                const response = await apiWithoutBaseURL.get('/Graph/api/findAllTags/')
                //查看请求的地址
                console.log('findAllTags API调用:', response.config.url)
                return response.data
            } catch (error) {
                console.error('查找所有标签失败:', error)
                throw error
            }
        },

        // 根据节点查找图谱关系【已使用】
        async findGraphByNode(nodeName, times = 0) {
            console.log('findGraphByNode API调用:', nodeName, times)
            try {
                let url = `/Graph/api/findGraphByNode/${nodeName}/`
                if (times > 0) {
                    url = `/Graph/api/findGraphByNode/${nodeName}/${times}/`
                }
                const response = await apiWithoutBaseURL.get(url)
                console.log('findGraphByNode API响应:', response)
                this.processGraphExpansionData(response.data.data)
                return response.data
            } catch (error) {
                console.error('根据节点查找图谱关系失败:', error)
                throw error
            }
        },

        // 查找节点
        async findNode(nodeName) {
            try {
                const response = await apiWithoutBaseURL.get(`/Graph/api/findNode/${nodeName}/`)
                return response.data
            } catch (error) {
                console.error('查找节点失败:', error)
                throw error
            }
        },

        // 修改节点
        async modifyNode(nodeId, nodeName, nodeDesc) {
            try {
                const response = await apiWithoutBaseURL.get(`/Graph/api/modifyNode/${nodeId}/${nodeName}/${nodeDesc}/`)
                return response.data
            } catch (error) {
                console.error('修改节点失败:', error)
                throw error
            }
        },

        // 删除节点
        async deleteNodeAPI(nodeId) {
            try {
                const response = await apiWithoutBaseURL.get(`/Graph/api/deleteNode/${nodeId}/`)
                return response.data
            } catch (error) {
                console.error('删除节点失败:', error)
                throw error
            }
        },

        // 添加节点
        async addNodeAPI(nodeName, nodeDesc = null) {
            try {
                // 使用POST请求传递参数，避免URL中特殊字符导致的问题
                const response = await apiWithoutBaseURL.post('/Graph/api/addNode/', {
                    nodeName: nodeName,
                    nodeDesc: nodeDesc
                })
                if (response.status == 200) {
                    console.log('response.data:', response.data.data)
                    var tempNode = {}
                    tempNode["id"] = response.data.data.id;
                    tempNode["name"] = response.data.data.name;
                    tempNode["desc"] = response.data.data.desc;
                    tempNode["symbolSize"] = 1;
                    tempNode["category"] = 0;//MARK这里暂时还没有明确怎么填
                    tempNode["groups"] = ["default"];
                    //设置被点击次数
                    tempNode["times"] = 0;
                    // 添加样式属性
                    tempNode["style"] = {
                        fill: '#5B8FF9',
                        stroke: '#fff',
                        lineWidth: 2
                    };
                    tempNode["locked"] = false;
                    this.addNode(tempNode);
                }

                return response.data
            } catch (error) {
                console.error('添加节点失败:', error)
                throw error
            }
        },

        // 添加子节点
        async addChildNode(nodeId, nodeName, graphId, nodeDesc = null) {
            try {
                // 使用POST请求传递参数，避免URL中特殊字符导致的问题
                const response = await apiWithoutBaseURL.post('/Graph/api/addChildNode/', {
                    nodeId: nodeId,
                    nodeName: nodeName,
                    graphId: graphId,
                    nodeDesc: nodeDesc
                })
                console.log('response:', response)
                if (response.status == 200) {
                    console.log('response.data:', response)
                    var tempNode = {}
                    tempNode["id"] = String(response.data.data.node.id);
                    tempNode["name"] = response.data.data.node.name;
                    tempNode["desc"] = response.data.data.node.desc;
                    tempNode["symbolSize"] = 1;
                    tempNode["category"] = 0;
                    tempNode["groups"] = ["default"];
                    //设置被点击次数
                    tempNode["times"] = 0;
                    // 添加样式属性
                    tempNode["style"] = {
                        fill: '#5B8FF9',
                        stroke: '#fff',
                        lineWidth: 2
                    };
                    tempNode["locked"] = false;
                    this.addNode(tempNode);

                    // 添加关系
                    var tempEdge = {}
                    tempEdge["id"] = response.data.data.link.id;
                    tempEdge["source"] = response.data.data.link.source;
                    tempEdge["target"] = response.data.data.link.target;
                    tempEdge["graphId"] = response.data.data.link.graphId;
                    tempEdge["value"] = response.data.data.link.value;
                    tempEdge["label"] = response.data.data.link.label;
                    this.addEdge(tempEdge);
                }
                return response.data
            } catch (error) {
                console.error('添加子节点失败:', error)
                throw error
            }
        },

        // 添加关系
        async addGraph(sourceId, targetId, graphId) {
            try {
                const response = await apiWithoutBaseURL.post('/Graph/api/addGraph/', {
                    source: sourceId,
                    target: targetId,
                    graphId: graphId
                })
                console.log('addGraph API响应:', response)
                if (response.status == 200) {
                    // 添加关系
                    var tempEdge = {}
                    tempEdge["source"] = response.data.data.source;
                    tempEdge["target"] = response.data.data.target;
                    tempEdge["graphId"] = response.data.data.graphId;
                    tempEdge["value"] = response.data.data.value;
                    tempEdge["label"] = response.data.data.label;
                    this.addEdge(tempEdge);
                }
                return response.data
            } catch (error) {
                console.error('添加关系失败:', error)
                throw error
            }
        },

        // 删除关系
        async deleteGraph(sourceId, targetId, graphId) {
            try {
                const response = await apiWithoutBaseURL.post('/Graph/api/deleteGraph/', {
                    source: sourceId,
                    target: targetId,
                    graphId: graphId
                })
                return response.data
            } catch (error) {
                console.error('删除关系失败:', error)
                throw error
            }
        },

        // 添加标签
        async addTag(tagName) {
            try {
                const response = await apiWithoutBaseURL.post('/Graph/api/addTag/', {
                    tagName: tagName
                })
                // 更新标签列表
                if (response.data && response.data.data) {
                    this.tags.push(response.data.data)
                }
                return response.data
            } catch (error) {
                console.error('添加标签失败:', error)
                throw error
            }
        },

        // 模糊查找节点
        async fuzzyFindNode(nodeName) {
            try {
                const response = await apiWithoutBaseURL.get(`/Graph/api/fuzzyFindNode/${nodeName}/`)
                return response.data
            } catch (error) {
                console.error('模糊查找节点失败:', error)
                throw error
            }
        },

        // 设置选中的标签
        setSelectedTags(tags) {
            this.selectedTags = tags
        },

        // 添加测试标签
        addTestTag() {
            const newTag = {
                id: Date.now(),
                name: `测试标签${Date.now()}`
            }
            this.tags.push(newTag)
            console.log('添加测试标签:', newTag)
        },

        // 添加标签到节点（本地状态）
        addTagToNodeLocal(nodeId, tagName) {
            const nodeIndex = this.nodes.findIndex(node => node.id === nodeId)
            if (nodeIndex !== -1) {
                if (!this.nodes[nodeIndex].tags) {
                    this.nodes[nodeIndex].tags = []
                }
                if (!this.nodes[nodeIndex].tags.includes(tagName)) {
                    this.nodes[nodeIndex].tags.push(tagName)
                    this.saveData()
                }
            }
        },

        // 从节点移除标签（本地状态）
        removeTagFromNodeLocal(nodeId, tagName) {
            const nodeIndex = this.nodes.findIndex(node => node.id === nodeId)
            if (nodeIndex !== -1 && this.nodes[nodeIndex].tags) {
                this.nodes[nodeIndex].tags = this.nodes[nodeIndex].tags.filter(tag => tag !== tagName)
                this.saveData()
            }
        },

        // 处理拓展图谱数据
        async processGraphExpansionData(expansionData) {
            console.log("processGraphExpansionData", expansionData)
            if (!expansionData || !Array.isArray(expansionData)) {
                console.warn('拓展数据格式不正确:', expansionData)
                return
            }

            // 创建一个Set来跟踪已存在的节点ID，避免重复添加
            const existingNodeIds = new Set(this.nodes.map(node => node.id))
            const existingEdgeIds = new Set(this.edges.map(edge => {
                // 创建一个唯一的边ID
                return `${edge.source}_${edge.target}_${edge.graphId}_${edge.label?.formatter || ''}`
            }))

            // 处理返回的数据项
            expansionData.forEach(item => {
                //获取新node
                var tempNode = {};
                var tempId = null;
                if (item["graphIndex"] == "source") {
                    tempId = item["target"];
                } else {
                    tempId = item["source"];
                }
                //获取新节点
                var existNode = existingNodeIds.has(tempId);
                if (!existNode) {
                    tempNode["id"] = tempId;
                    tempNode["name"] = item["newName"];
                    tempNode["desc"] = item["newDesc"];
                    tempNode["symbolSize"] = item["value"];
                    tempNode["category"] = 0;//MARK这里暂时还没有明确怎么填
                    tempNode["graphs"] = ['default']
                    //设置被点击次数
                    tempNode["times"] = 0;
                    // 添加样式属性
                    tempNode["style"] = {
                        fill: '#5B8FF9',
                        stroke: '#fff',
                        lineWidth: 2
                    };
                    tempNode["locked"] = false;
                    this.addNode(tempNode);
                    existingNodeIds.add(tempId)
                }

                //获取新关系
                var tempLink = {};
                // 创建一个唯一的边ID用于检查重复
                const linkId = `${item["source"]}_${item["target"]}_${item["graphId"]}_${item["label"]?.["formatter"] || ''}`;
                var existLink = existingEdgeIds.has(linkId);

                if (!existLink) {
                    tempLink["source"] = item["source"];
                    tempLink["target"] = item["target"];
                    tempLink["value"] = item["value"];
                    tempLink["label"] = item["label"];
                    tempLink["graphId"] = item["graphId"];
                    // 添加样式属性
                    tempLink["style"] = {
                        stroke: '#A3B1BF',
                        lineWidth: 2
                    };
                    this.addEdge(tempLink);
                    existingEdgeIds.add(linkId);
                }
            })
            console.log("now data", this.nodes, this.edges);
            // 保存数据
            this.saveData()
        }
    }
})